import { useEffect, useState } from 'react';

import type { Classroom } from '@innovamat/glow-api-client';
import { Level, useCourses } from '@innovamat/glow-api-client';

import { SCHEDULE_DEFAULT_VALUES } from '../../../constants';
import { useClassroomScheduleValues } from '../../../hooks/use-classroom-schedule-values';
import { useOrganization } from '../../../hooks/use-organization';

const STEPS = {
  CONFIGURATION: 'configuration',
  STUDENTS: 'students',
} as const;

type Step = (typeof STEPS)[keyof typeof STEPS];

type TempClassroom = {
  avatar: string | null;
  courseId: string;
  alias: string;
  icon: number | null;
  name: string;
  level: Level;
  digitalPracticeFirstDayOfWeek: string;
};

type Props = {
  classroom?: Classroom;
  show: boolean;
  availableCoursesIds?: string[];
};

function useClassroomModalState({
  classroom,
  show,
  availableCoursesIds,
}: Props) {
  const { organization } = useOrganization();
  const [state, setState] = useState<TempClassroom>({
    name: '',
    courseId: '',
    alias: '',
    icon: null,
    avatar: null,
    level: Level.DEFAULT,
    digitalPracticeFirstDayOfWeek: '',
  });

  const { validCoursesByCourse, toOptions, courses } = useCourses({
    organizationId: organization?.id || '',
    regionCode: organization?.region || '',
  });

  const coursesOptions = toOptions(
    validCoursesByCourse(classroom?.courseOrder || null)
  ).filter((option) => {
    if (!availableCoursesIds) return true;
    return availableCoursesIds.includes(option.value);
  });

  const schedule = useClassroomScheduleValues();

  const [currentStep, setCurrentStep] = useState<Step>(
    STEPS.CONFIGURATION
  );

  const timezone = organization?.timezone || '';

  const selectedCourse = courses.find((course) => {
    return course.id === state.courseId;
  });

  useEffect(() => {
    setState({
      avatar: classroom?.avatar || null,
      courseId: classroom?.courseId || '',
      icon: classroom?.icon || null,
      name: classroom?.name || '',
      alias: classroom?.alias || '',
      level: (classroom?.level as Level) || Level.DEFAULT,
      digitalPracticeFirstDayOfWeek:
        classroom?.digitalPracticeFirstDayOfWeek || '',
    });

    schedule.handleInitValues({
      scheduleStart:
        classroom?.scheduleStart || SCHEDULE_DEFAULT_VALUES.scheduleStart,
      scheduleEnd:
        classroom?.scheduleEnd || SCHEDULE_DEFAULT_VALUES.scheduleEnd,
      timeZone: timezone,
    });

    if (show && currentStep === STEPS.STUDENTS) {
      return;
    }

    if (show) {
      setCurrentStep(STEPS.CONFIGURATION);
    }
  }, [show, classroom, timezone]);

  return {
    state,
    setState,
    schedule,
    currentStep,
    setCurrentStep,
    coursesOptions,
    selectedCourse,
  };
}

export { useClassroomModalState };
export type { TempClassroom };

